import React, { useState } from "react";
import { View, TextInput, StyleSheet, Platform, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";

interface SSNInputProps {
  label?: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  editable?: boolean;
  /** Show skeleton loading state */
  loading?: boolean;
}

// SSN must be exactly 9 digits (formatted as XXX-XX-XXXX)
const SSN_REGEX = /^\d{3}-\d{2}-\d{4}$/;

// Format SSN as user types (XXX-XX-XXXX)
function formatSSN(value: string): string {
  const cleaned = value.replace(/\D/g, "").slice(0, 9);
  if (cleaned.length > 5) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
  } else if (cleaned.length > 3) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }
  return cleaned;
}

// Validate SSN format
function validateSSN(value: string): boolean {
  return SSN_REGEX.test(value);
}

// Mask SSN for display (show last 4 digits)
function maskSSN(value: string): string {
  if (!value) return "";
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length >= 4) {
    return `***-**-${cleaned.slice(-4)}`;
  }
  return value;
}

export function SSNInput({
  label,
  value,
  onChangeText,
  placeholder = "XXX-XX-XXXX",
  editable = true,
  loading = false,
}: SSNInputProps) {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const [showSSN, setShowSSN] = useState(false);

  const isValidFormat = value.length === 0 || validateSSN(value);
  const hasError = value.length > 0 && !isValidFormat;

  // Handle text change with formatting
  const handleChangeText = (text: string) => {
    const formatted = formatSSN(text);
    onChangeText(formatted);
  };

  // Loading state - show skeleton
  if (loading) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <Skeleton width="100%" height={44} radius={8} />
      </View>
    );
  }

  // Readonly state - show masked SSN
  if (!editable) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <View
          style={[styles.inputContainer, { borderColor: colors.border, backgroundColor: colors.backgroundSecondary }]}
        >
          <MaterialCommunityIcons
            name="card-account-details-outline"
            size={18}
            color={colors.textSecondary}
            style={styles.icon}
          />
          <ThemedText style={[styles.ssnText, { color: value ? colors.text : colors.placeholder }]}>
            {value ? maskSSN(value) : "Not provided"}
          </ThemedText>
        </View>
      </View>
    );
  }

  const borderColor = hasError ? colors.ruby?.border || "red" : isFocused ? colors.primary : colors.border;

  return (
    <View style={styles.container}>
      {(label || hasError) && (
        <View style={styles.labelRow}>
          {label && <ThemedText style={styles.label}>{label}</ThemedText>}
          {hasError && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText || colors.ruby?.text || "red" }]}>
              Invalid SSN format
            </ThemedText>
          )}
        </View>
      )}
      <View style={[styles.inputContainer, { borderColor, backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons
          name="card-account-details-outline"
          size={18}
          color={hasError ? colors.ruby?.text || "red" : isFocused ? colors.primary : colors.textSecondary}
          style={styles.icon}
        />
        <TextInput
          value={value}
          onChangeText={handleChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.placeholder}
          style={[styles.input, { color: colors.text }]}
          keyboardType="number-pad"
          maxLength={11}
          secureTextEntry={!showSSN}
          autoCapitalize="none"
          autoCorrect={false}
          editable={editable}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {value.length > 0 && (
          <Pressable onPress={() => setShowSSN(!showSSN)} style={styles.eyeButton}>
            <MaterialCommunityIcons name={showSSN ? "eye-off" : "eye"} size={20} color={colors.textSecondary} />
          </Pressable>
        )}
        {value.length > 0 && isValidFormat && (
          <MaterialCommunityIcons
            name="check-circle"
            size={18}
            color={colors.emerald?.text || "green"}
            style={{ marginLeft: 4 }}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
  },
  icon: {
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 15,
    height: "100%",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  ssnText: {
    fontSize: 15,
    flex: 1,
  },
  eyeButton: {
    padding: 4,
  },
  errorText: {
    fontSize: 12,
  },
});
